import { useState, useCallback, useEffect } from 'react';
import { Node, Edge, useReactFlow } from 'reactflow';
import { NodeGroup } from '../types/groupTypes';
import { FlowData } from '../types/flowTypes';
import { trainingFlowsApi } from '../services/trainingFlowsApi';
import { messagesApi } from '../services/messagesApi';
import { updateNodesWithFlowData } from '../utils/nodeSequencing';
import { reconcileGroupsWithEdges } from '../utils/groupReconciliation';

const LOCAL_STORAGE_KEY = 'agent_training_autosave';

export const useTrainingFlow = (
  nodes: Node[],
  edges: Edge[],
  groups: NodeGroup[],
  setNodes: (nodes: Node[]) => void,
  setEdges: (edges: Edge[]) => void,
  setGroups: (groups: NodeGroup[]) => void,
  selectedAgent?: string
) => {
  const [currentFlowId, setCurrentFlowId] = useState<string | null>(null);
  const [currentFlowName, setCurrentFlowName] = useState<string>('Sin título');
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [lastSavedState, setLastSavedState] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const { getViewport } = useReactFlow();

  // Crear una "huella digital" del estado actual
  const getCurrentStateHash = useCallback(() => {
    return JSON.stringify({
      nodes: nodes.map(n => ({ id: n.id, position: n.position, data: n.data })),
      edges: edges.map(e => ({ id: e.id, source: e.source, target: e.target })),
      groups: groups.map(g => ({ 
        id: g.id, 
        nodeIds: g.nodeIds, 
        instruction: g.instruction, 
        leadStatus: g.leadStatus 
      }))
    });
  }, [nodes, edges, groups]);

  // Función auxiliar para mapear mensajes guardados a los nodos
  const mapMessagesToNodes = useCallback(async (nodes: Node[], flowId: string) => {
    try {
      const messages = await messagesApi.getMessagesByFlowId(flowId);
      
      return nodes.map(node => {
        const message = messages.find(m => m.node_id === node.id);
        if (!message) return node;

        // Mapear datos según el tipo de nodo
        const updatedData = { ...node.data };
        
        if (message.node_type === 'text' && message.text_content) {
          updatedData.message = message.text_content;
          updatedData.messageId = message.id;
        } else if (message.node_type === 'image') {
          updatedData.imageUrl = message.media_url;
          updatedData.fileName = message.file_name;
          updatedData.caption = message.text_content;
          updatedData.aiDescription = message.ai_description;
          updatedData.processingStatus = message.ai_processing_status;
          updatedData.messageId = message.id;
        } else if (message.node_type === 'audio') {
          updatedData.audioUrl = message.media_url;
          updatedData.fileName = message.file_name;
          updatedData.aiTranscription = message.ai_description;
          updatedData.processingStatus = message.ai_processing_status;
          updatedData.messageId = message.id;
        } else if (message.node_type === 'video') {
          updatedData.videoUrl = message.media_url;
          updatedData.fileName = message.file_name;
          updatedData.caption = message.text_content;
          updatedData.messageId = message.id;
        } else if (message.node_type === 'document') {
          updatedData.documentUrl = message.media_url;
          updatedData.fileName = message.file_name;
          updatedData.caption = message.text_content;
          updatedData.messageId = message.id;
        }

        return {
          ...node,
          data: updatedData
        };
      });
    } catch (error) {
      console.error('Error al mapear mensajes:', error);
      return nodes;
    }
  }, []);

  // Cargar automáticamente el flujo activo cuando se selecciona un agente
  useEffect(() => {
    const loadActiveFlow = async () => {
      if (!selectedAgent) return;
      
      setIsLoading(true);
      try {
        const activeFlow = await trainingFlowsApi.getActiveFlow(selectedAgent);
        if (activeFlow) {
          const flowData = activeFlow.flow_data as FlowData;
          
          // Cargar mensajes guardados y mapearlos a los nodos
          const nodesWithMessages = await mapMessagesToNodes(flowData.nodes || [], activeFlow.id);

          // Reconciliar grupos con las conexiones existentes
          const reconciledGroups = reconcileGroupsWithEdges(
            flowData.groups || [],
            nodesWithMessages,
            flowData.edges || []
          );

          setNodes(nodesWithMessages);
          setEdges(flowData.edges || []);
          setGroups(reconciledGroups);
          setCurrentFlowId(activeFlow.id);
          setCurrentFlowName(activeFlow.nombre);
          setIsActive(true);
          
          // Guardar el estado inicial para comparación
          setTimeout(() => {
            const initialState = JSON.stringify({
              nodes: nodesWithMessages.map(n => ({ id: n.id, position: n.position, data: n.data })),
              edges: (flowData.edges || []).map(e => ({ id: e.id, source: e.source, target: e.target })),
              groups: (flowData.groups || []).map(g => ({ 
                id: g.id, 
                nodeIds: g.nodeIds, 
                instruction: g.instruction, 
                leadStatus: g.leadStatus 
              }))
            });
            setLastSavedState(initialState);
            setHasUnsavedChanges(false);
          }, 500);
        } else {
          // Si no hay flujo activo, limpiar todo
          setNodes([]);
          setEdges([]);
          setGroups([]);
          setCurrentFlowId(null);
          setCurrentFlowName('Sin título');
          setIsActive(false);
          setLastSavedState('');
          setHasUnsavedChanges(false);
        }
      } catch (error) {
        console.error('Error al cargar flujo activo:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadActiveFlow();
  }, [selectedAgent, setNodes, setEdges, setGroups, mapMessagesToNodes]);

  // Auto-save local cada 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      if (nodes.length > 0) {
        const flowData: FlowData = {
          nodes,
          edges,
          groups,
          viewport: getViewport()
        };
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
          data: flowData,
          timestamp: Date.now()
        }));
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [nodes, edges, groups, getViewport]);

  // Detectar cambios comparando con el último estado guardado
  useEffect(() => {
    // No detectar cambios mientras se está cargando
    if (isLoading || !currentFlowId) {
      return;
    }

    // Esperar un poco antes de empezar a detectar cambios
    const timer = setTimeout(() => {
      const currentState = getCurrentStateHash();
      if (lastSavedState && currentState !== lastSavedState) {
        setHasUnsavedChanges(true);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [nodes, edges, groups, lastSavedState, currentFlowId, isLoading, getCurrentStateHash]);

  // Función auxiliar para guardar todos los nodos en training_messages
  const saveAllNodesToDatabase = useCallback(async (flowId: string) => {
    console.log('🔄 Recalculando sequence_order antes de guardar...');
    
    // CRÍTICO: Recalcular sequence_order basándose en el grafo actual
    const updatedNodes = updateNodesWithFlowData(nodes, edges, groups, flowId);
    
    console.log('📊 Nodos con sequence_order actualizado:', updatedNodes.map(n => ({
      id: n.id,
      type: n.type,
      sequenceOrder: (n.data as any).sequenceOrder,
      groupId: (n.data as any).groupId
    })));
    
    console.log('💾 Guardando todos los nodos en la base de datos...');
    const savePromises = updatedNodes.map(async (node) => {
      const nodeData = node.data as any;
      const nodeType = node.type;
      
      // Determinar el tipo de nodo para training_messages
      let messageNodeType: 'text' | 'image' | 'audio' | 'video' | 'document' = 'text';
      if (nodeType === 'textMessage') messageNodeType = 'text';
      else if (nodeType === 'imageMessage') messageNodeType = 'image';
      else if (nodeType === 'audioMessage') messageNodeType = 'audio';
      else if (nodeType === 'videoMessage') messageNodeType = 'video';
      else if (nodeType === 'documentMessage') messageNodeType = 'document';
      
      // Buscar el grupo al que pertenece este nodo para obtener product_keys y lead_status
      const nodeGroup = groups.find(g => g.nodeIds.includes(node.id));
      
      // Preparar el mensaje para guardar
      const message: any = {
        id: nodeData.messageId || undefined,
        node_id: node.id,
        node_type: messageNodeType,
        flow_id: flowId,
        sequence_order: nodeData.sequenceOrder || 1,
        group_id: nodeData.groupId || null,
        product_keys: nodeGroup?.productKeys || [],
        lead_status: nodeGroup?.leadStatus || null,
      };
      
      // Agregar contenido según el tipo de nodo
      if (messageNodeType === 'text') {
        message.text_content = nodeData.message || '';
      } else if (messageNodeType === 'image') {
        message.media_url = nodeData.imageUrl || '';
        message.file_name = nodeData.fileName || null;
        message.text_content = nodeData.caption || null;
        message.ai_description = nodeData.aiDescription || null;
        message.ai_processing_status = nodeData.processingStatus || 'pending';
      } else if (messageNodeType === 'audio') {
        message.media_url = nodeData.audioUrl || '';
        message.file_name = nodeData.fileName || null;
        message.ai_description = nodeData.aiTranscription || null;
        message.ai_processing_status = nodeData.processingStatus || 'pending';
      } else if (messageNodeType === 'video') {
        message.media_url = nodeData.videoUrl || '';
        message.file_name = nodeData.fileName || null;
        message.text_content = nodeData.caption || null;
      } else if (messageNodeType === 'document') {
        message.media_url = nodeData.documentUrl || '';
        message.file_name = nodeData.fileName || null;
        message.text_content = nodeData.caption || null;
      }
      
      try {
        const savedMessage = await messagesApi.upsertMessage(message);
        console.log('Nodo guardado:', node.id, savedMessage);
        return savedMessage;
      } catch (error) {
        console.error('Error guardando nodo:', node.id, error);
        throw error;
      }
    });
    
    await Promise.all(savePromises);
    console.log('✅ Todos los nodos guardados exitosamente');
  }, [nodes, edges, groups]);

  // Guardar flujo en Supabase
  const saveFlow = useCallback(async (nombre: string, agentId?: string) => {
    setIsSaving(true);
    try {
      const flowData: FlowData = {
        nodes,
        edges,
        groups,
        viewport: getViewport()
      };

      let savedFlow;
      if (currentFlowId) {
        savedFlow = await trainingFlowsApi.updateFlow({
          flowId: currentFlowId,
          nombre,
          agentId,
          flowData
        });
      } else {
        savedFlow = await trainingFlowsApi.saveFlow({
          nombre,
          agentId,
          flowData
        });
      }

      setCurrentFlowId(savedFlow.id);
      setCurrentFlowName(savedFlow.nombre);
      
      // Guardar todos los nodos en training_messages
      await saveAllNodesToDatabase(savedFlow.id);
      
      // Actualizar el estado guardado y resetear cambios
      const newSavedState = getCurrentStateHash();
      setLastSavedState(newSavedState);
      setHasUnsavedChanges(false);
      
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      
      return savedFlow;
    } catch (error) {
      console.error('Error al guardar flujo:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [nodes, edges, groups, currentFlowId, getViewport, getCurrentStateHash, saveAllNodesToDatabase]);

  // Cargar flujo
  const loadFlow = useCallback(async (flowId: string) => {
    setIsLoading(true);
    const flow = await trainingFlowsApi.loadFlow(flowId);
    const flowData = flow.flow_data as FlowData;
    
    // Cargar mensajes guardados y mapearlos a los nodos
    const nodesWithMessages = await mapMessagesToNodes(flowData.nodes || [], flowId);

    // Reconciliar grupos con las conexiones existentes
    const reconciledGroups = reconcileGroupsWithEdges(
      flowData.groups || [],
      nodesWithMessages,
      flowData.edges || []
    );

    setNodes(nodesWithMessages);
    setEdges(flowData.edges || []);
    setGroups(reconciledGroups);
    setCurrentFlowId(flow.id);
    setCurrentFlowName(flow.nombre);
    setIsActive(flow.is_active || false);
    
    // Guardar el estado cargado para comparación futura
    setTimeout(() => {
      const loadedState = JSON.stringify({
        nodes: nodesWithMessages.map(n => ({ id: n.id, position: n.position, data: n.data })),
        edges: (flowData.edges || []).map(e => ({ id: e.id, source: e.source, target: e.target })),
        groups: (flowData.groups || []).map(g => ({ 
          id: g.id, 
          nodeIds: g.nodeIds, 
          instruction: g.instruction, 
          leadStatus: g.leadStatus 
        }))
      });
      setLastSavedState(loadedState);
      setHasUnsavedChanges(false);
      setIsLoading(false);
    }, 500);
    
    return flow;
  }, [setNodes, setEdges, setGroups, mapMessagesToNodes]);

  // Activar flujo actual
  const activateCurrentFlow = useCallback(async () => {
    if (!currentFlowId) {
      throw new Error('Debes guardar el flujo antes de activarlo');
    }
    
    // Antes de activar, guardar todos los nodos
    console.log('Guardando todos los nodos antes de activar...');
    await saveAllNodesToDatabase(currentFlowId);
    
    await trainingFlowsApi.activateFlow(currentFlowId);
    setIsActive(true);
  }, [currentFlowId, saveAllNodesToDatabase]);

  // Desactivar flujo actual
  const deactivateCurrentFlow = useCallback(async () => {
    if (!currentFlowId) return;
    await trainingFlowsApi.deactivateFlow(currentFlowId);
    setIsActive(false);
  }, [currentFlowId]);

  // Crear nuevo flujo
  const newFlow = useCallback(() => {
    setNodes([]);
    setEdges([]);
    setGroups([]);
    setCurrentFlowId(null);
    setCurrentFlowName('Nuevo flujo');
    setHasUnsavedChanges(false);
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  }, [setNodes, setEdges, setGroups]);

  // Exportar como JSON
  const exportFlow = useCallback(async () => {
    if (!currentFlowId) {
      throw new Error('Debes guardar el flujo antes de exportarlo');
    }
    const flow = await trainingFlowsApi.loadFlow(currentFlowId);
    trainingFlowsApi.exportFlow(flow);
  }, [currentFlowId]);

  // Importar desde JSON
  const importFlow = useCallback(async (file: File) => {
    const flowData = await trainingFlowsApi.importFlow(file);
    setNodes(flowData.nodes || []);
    setEdges(flowData.edges || []);
    setGroups(flowData.groups || []);
    setCurrentFlowId(null);
    setCurrentFlowName(file.name.replace('.json', ''));
    setHasUnsavedChanges(true);
  }, [setNodes, setEdges, setGroups]);

  // Recuperar borrador local
  const recoverDraft = useCallback(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      const { data } = JSON.parse(saved);
      setNodes(data.nodes || []);
      setEdges(data.edges || []);
      setGroups(data.groups || []);
      setHasUnsavedChanges(true);
      return true;
    }
    return false;
  }, [setNodes, setEdges, setGroups]);

  // Sincronizar cambios del previsualizador con el flow_data
  const syncFlowData = useCallback(async () => {
    if (!currentFlowId) return;
    
    try {
      const flowData: FlowData = {
        nodes,
        edges,
        groups,
        viewport: getViewport()
      };

      await trainingFlowsApi.updateFlow({
        flowId: currentFlowId,
        nombre: currentFlowName,
        flowData
      });

      // Actualizar el estado guardado
      const newSavedState = getCurrentStateHash();
      setLastSavedState(newSavedState);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error sincronizando flow_data:', error);
      throw error;
    }
  }, [currentFlowId, currentFlowName, nodes, edges, groups, getViewport, getCurrentStateHash]);

  return {
    currentFlowId,
    currentFlowName,
    isSaving,
    isLoading,
    hasUnsavedChanges,
    isActive,
    saveFlow,
    loadFlow,
    newFlow,
    exportFlow,
    importFlow,
    recoverDraft,
    activateCurrentFlow,
    deactivateCurrentFlow,
    syncFlowData
  };
};
